import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Server, Database } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import ProjectParametersSection from './ProjectParametersSection';
import InfrastructureSection from './InfrastructureSection';
import AdditionalCostsSection from './AdditionalCostsSection';
import MaintenanceSection from './MaintenanceSection';
import SecuritySection from './SecuritySection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета стоимости проекта Big Data
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  costBreakdown,
  setCostBreakdown,
  projectTypes,
  cloudProviders,
  dataComplexityLevels,
  securityLevels,
  licenseTypes,
  regionOptions,
  saveProject 
}) => {
  
  // Состояние для имени сохраняемого проекта
  const [saveProjectName, setSaveProjectName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем размер команды автоматически при изменении типа проекта
      if (name === 'projectType') {
        const selectedProjectType = projectTypes.find(type => type.id === fieldValue);
        if (selectedProjectType) {
          const currentTeamSize = parseIntOrDefault(prev.teamSize, 0);
          const isTeamSizeDefault = projectTypes.some(pt => currentTeamSize === parseIntOrDefault(pt.defaultTeamSize, 0));
          if (isTeamSizeDefault || !prev.teamSize || currentTeamSize <= 0) {
            newState.teamSize = selectedProjectType.defaultTeamSize.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeMaintenanceCosts' && !checked) {
        newState.maintenancePeriod = '12';
      }
      
      if (name === 'includeTrainingCosts' && !checked) {
        newState.trainingPeopleCount = '10';
      }
      
      if (name === 'includeLicenseCosts' && !checked) {
        newState.licenseType = 'per_user';
        newState.licenseCount = '10';
      }
      
      if (name === 'includeConsultingCosts' && !checked) {
        newState.consultingDays = '10';
      }
      
      // Преобразование единиц измерения данных
      if (name === 'dataVolumeUnit') {
        const currentVolume = parseFloatOrDefault(prev.dataVolume, 0);
        if (prev.dataVolumeUnit === 'gb' && value === 'tb') {
          newState.dataVolume = (currentVolume / 1024).toFixed(2).toString();
        } else if (prev.dataVolumeUnit === 'gb' && value === 'pb') {
          newState.dataVolume = (currentVolume / (1024 * 1024)).toFixed(4).toString();
        } else if (prev.dataVolumeUnit === 'tb' && value === 'gb') {
          newState.dataVolume = (currentVolume * 1024).toFixed(0).toString();
        } else if (prev.dataVolumeUnit === 'tb' && value === 'pb') {
          newState.dataVolume = (currentVolume / 1024).toFixed(2).toString();
        } else if (prev.dataVolumeUnit === 'pb' && value === 'gb') {
          newState.dataVolume = (currentVolume * 1024 * 1024).toFixed(0).toString();
        } else if (prev.dataVolumeUnit === 'pb' && value === 'tb') {
          newState.dataVolume = (currentVolume * 1024).toFixed(0).toString();
        }
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [projectTypes, errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      dataVolume, dataVolumeUnit, dataProcessingRate, projectDuration, dataSourcesCount,
      projectType, cloudProvider, teamSize, dataComplexity, securityLevel,
      includeMaintenanceCosts, maintenancePeriod,
      includeTrainingCosts, trainingPeopleCount,
      includeLicenseCosts, licenseType, licenseCount,
      includeConsultingCosts, consultingDays,
      region, currency, contingencyPercent
    } = formData;

    // Парсинг числовых значений
    const numDataVolume = parseFloatOrDefault(dataVolume, -1);
    const numDataProcessingRate = parseFloatOrDefault(dataProcessingRate, -1);
    const numProjectDuration = parseFloatOrDefault(projectDuration, -1);
    const numDataSourcesCount = parseIntOrDefault(dataSourcesCount, -1);
    const numTeamSize = parseIntOrDefault(teamSize, -1);
    const numContingencyPercent = parseFloatOrDefault(contingencyPercent, -1);

    // Валидация базовых полей
    if (numDataVolume <= 0) newErrors.dataVolume = 'Объем данных должен быть положительным числом.';
    if (!dataVolumeUnit) newErrors.dataVolumeUnit = 'Выберите единицу измерения.';
    
    if (numDataProcessingRate <= 0) newErrors.dataProcessingRate = 'Скорость обработки должна быть положительной.';
    if (numDataProcessingRate > 10000) newErrors.dataProcessingRate = 'Слишком высокая скорость обработки.';
    
    if (numProjectDuration <= 0) newErrors.projectDuration = 'Срок проекта должен быть положительным.';
    if (numProjectDuration > 60) newErrors.projectDuration = 'Максимальный срок 60 месяцев.';
    
    if (numDataSourcesCount <= 0) newErrors.dataSourcesCount = 'Количество источников должно быть положительным.';
    if (numDataSourcesCount > 1000) newErrors.dataSourcesCount = 'Слишком много источников данных.';
    
    if (!projectType) newErrors.projectType = 'Выберите тип проекта.';
    if (!cloudProvider) newErrors.cloudProvider = 'Выберите облачного провайдера.';
    
    if (numTeamSize <= 0) newErrors.teamSize = 'Размер команды должен быть положительным.';
    if (numTeamSize > 100) newErrors.teamSize = 'Максимальный размер команды 100 человек.';
    
    if (!dataComplexity) newErrors.dataComplexity = 'Выберите уровень сложности данных.';
    if (!securityLevel) newErrors.securityLevel = 'Выберите уровень безопасности.';
    
    if (numContingencyPercent < 0) newErrors.contingencyPercent = 'Процент на непредвиденные расходы не может быть отрицательным.';
    if (numContingencyPercent > 50) newErrors.contingencyPercent = 'Слишком высокий процент на непредвиденные расходы (макс. 50%).';
    
    if (!region) newErrors.region = 'Выберите регион размещения.';
    if (!currency) newErrors.currency = 'Выберите валюту расчета.';

    // Валидация обслуживания
    if (includeMaintenanceCosts) {
      const numMaintenancePeriod = parseIntOrDefault(maintenancePeriod, -1);
      if (numMaintenancePeriod <= 0) newErrors.maintenancePeriod = 'Период обслуживания должен быть положительным.';
      if (numMaintenancePeriod > 60) newErrors.maintenancePeriod = 'Максимальный период обслуживания 60 месяцев.';
    }

    // Валидация обучения
    if (includeTrainingCosts) {
      const numTrainingPeopleCount = parseIntOrDefault(trainingPeopleCount, -1);
      if (numTrainingPeopleCount <= 0) newErrors.trainingPeopleCount = 'Количество обучаемых должно быть положительным.';
      if (numTrainingPeopleCount > 1000) newErrors.trainingPeopleCount = 'Слишком большое количество обучаемых.';
    }

    // Валидация лицензий
    if (includeLicenseCosts) {
      if (!licenseType) newErrors.licenseType = 'Выберите тип лицензирования.';
      
      const numLicenseCount = parseIntOrDefault(licenseCount, -1);
      if (numLicenseCount <= 0) newErrors.licenseCount = 'Количество лицензий должно быть положительным.';
      if (numLicenseCount > 10000) newErrors.licenseCount = 'Слишком большое количество лицензий.';
    }

    // Валидация консалтинга
    if (includeConsultingCosts) {
      const numConsultingDays = parseIntOrDefault(consultingDays, -1);
      if (numConsultingDays <= 0) newErrors.consultingDays = 'Количество дней консалтинга должно быть положительным.';
      if (numConsultingDays > 1000) newErrors.consultingDays = 'Слишком большое количество дней консалтинга.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет стоимости проекта
  const calculateProjectCost = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setCostBreakdown({});

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const dataVolume = parseFloatOrDefault(formData.dataVolume);
        // Пересчитываем все объемы в ТБ для расчетов
        let volumeInTB = dataVolume;
        if (formData.dataVolumeUnit === 'gb') {
          volumeInTB = dataVolume / 1024;
        } else if (formData.dataVolumeUnit === 'pb') {
          volumeInTB = dataVolume * 1024;
        }
        
        const dataProcessingRate = parseFloatOrDefault(formData.dataProcessingRate);
        const projectDuration = parseIntOrDefault(formData.projectDuration);
        const dataSourcesCount = parseIntOrDefault(formData.dataSourcesCount);
        const teamSize = parseIntOrDefault(formData.teamSize);
        const contingencyPercent = parseFloatOrDefault(formData.contingencyPercent) / 100;
        
        // Получаем множители стоимости из выбранных опций
        const projectTypeObj = projectTypes.find(t => t.id === formData.projectType) || projectTypes[0];
        const cloudProviderObj = cloudProviders.find(p => p.id === formData.cloudProvider) || cloudProviders[0];
        const dataComplexityObj = dataComplexityLevels.find(c => c.id === formData.dataComplexity) || dataComplexityLevels[1];
        const securityLevelObj = securityLevels.find(s => s.id === formData.securityLevel) || securityLevels[0];
        const regionObj = regionOptions.find(r => r.id === formData.region) || regionOptions[0];
        
        // --- Расчет стоимости инфраструктуры ---
        // Базовая стоимость хранения данных в ТБ/месяц (оценочно)
        const baseStorageCostPerTBPerMonth = 20; // USD
        
        // Стоимость хранения с учетом провайдера и региона
        const storageMultiplier = cloudProviderObj.costMultiplier * regionObj.costMultiplier;
        const adjustedStorageCost = baseStorageCostPerTBPerMonth * storageMultiplier;
        
        // Полная стоимость хранения за весь период проекта
        const totalStorageCost = volumeInTB * adjustedStorageCost * projectDuration;
        
        // --- Расчет стоимости обработки данных ---
        // Базовая стоимость обработки в USD/ТБ
        const baseProcessingCostPerTB = 15; // USD
        
        // Корректируем стоимость обработки в зависимости от типа проекта и сложности данных
        let processingMultiplier = 1.0;
        
        // Множитель для типа проекта
        switch (formData.projectType) {
          case 'analytics': processingMultiplier *= 0.8; break;
          case 'ml': processingMultiplier *= 1.3; break;
          case 'realtime': processingMultiplier *= 1.5; break;
          case 'datawarehouse': processingMultiplier *= 0.7; break;
          case 'dataquality': processingMultiplier *= 0.9; break;
          case 'dataintegration': processingMultiplier *= 1.1; break;
          default: processingMultiplier *= 1.0;
        }
        
        // Множитель для сложности данных
        processingMultiplier *= dataComplexityObj.costMultiplier;
        
        // Множитель для безопасности
        processingMultiplier *= securityLevelObj.costMultiplier;
        
        // Количество обрабатываемых ТБ данных в месяц (приблизительно)
        // Принимаем, что dataProcessingRate - это ГБ/час
        const processingRateInTBPerMonth = (dataProcessingRate / 1024) * 24 * 30;
        
        // Полная стоимость обработки с учетом всех множителей
        const processingCostPerMonth = processingRateInTBPerMonth * baseProcessingCostPerTB * processingMultiplier * cloudProviderObj.costMultiplier;
        const totalProcessingCost = processingCostPerMonth * projectDuration;
        
        // --- Расчет стоимости интеграции источников данных ---
        // Базовая стоимость интеграции одного источника данных
        const baseSourceIntegrationCost = 500; // USD
        
        // Множитель сложности интеграции в зависимости от сложности данных
        const integrationComplexityMultiplier = dataComplexityObj.costMultiplier;
        
        // Полная стоимость интеграции всех источников данных
        const totalSourceIntegrationCost = dataSourcesCount * baseSourceIntegrationCost * integrationComplexityMultiplier;
        
        // --- Расчет стоимости персонала ---
        // Средняя месячная зарплата специалиста по Big Data (USD)
        const baseMonthlySpecialistCost = 7000; // USD
        
        // Корректируем стоимость в зависимости от региона
        const regionalSalaryMultiplier = regionObj.costMultiplier;
        
        // Полная стоимость персонала за весь проект
        const totalPersonnelCost = teamSize * baseMonthlySpecialistCost * regionalSalaryMultiplier * projectDuration;
        
        // --- Расчет дополнительных затрат ---
        let totalAdditionalCosts = 0;
        
        // Стоимость обслуживания
        let maintenanceCost = 0;
        if (formData.includeMaintenanceCosts) {
          const maintenancePeriod = parseIntOrDefault(formData.maintenancePeriod);
          // Обслуживание обычно составляет около 15-20% от стоимости инфраструктуры в год
          const monthlyMaintenanceCost = (totalStorageCost + totalProcessingCost) * 0.015; // 18% в год / 12 месяцев
          maintenanceCost = monthlyMaintenanceCost * maintenancePeriod;
        }
        
        // Стоимость обучения
        let trainingCost = 0;
        if (formData.includeTrainingCosts) {
          const trainingPeopleCount = parseIntOrDefault(formData.trainingPeopleCount);
          // Усредненная стоимость обучения на одного человека
          const costPerPerson = 1200; // USD
          trainingCost = trainingPeopleCount * costPerPerson;
        }
        
        // Стоимость лицензий
        let licenseCost = 0;
        if (formData.includeLicenseCosts) {
          const licenseCount = parseIntOrDefault(formData.licenseCount);
          let costPerLicense = 0;
          
          // Определяем стоимость на основе типа лицензии
          switch (formData.licenseType) {
            case 'per_user':
              costPerLicense = 1000; // USD за пользователя
              licenseCost = licenseCount * costPerLicense;
              break;
            case 'per_core':
              costPerLicense = 2000; // USD за ядро
              licenseCost = licenseCount * costPerLicense;
              break;
            case 'per_cluster':
              // Для кластеров стоимость зависит от размера данных
              licenseCost = 5000 + (volumeInTB * 100); // Базовая цена + стоимость за ТБ
              break;
            case 'subscription':
              // Подписка обычно ежемесячная
              costPerLicense = 200; // USD в месяц за пользователя
              licenseCost = licenseCount * costPerLicense * projectDuration;
              break;
            default:
              costPerLicense = 1000;
              licenseCost = licenseCount * costPerLicense;
          }
        }
        
        // Стоимость консалтинга
        let consultingCost = 0;
        if (formData.includeConsultingCosts) {
          const consultingDays = parseIntOrDefault(formData.consultingDays);
          // Средняя стоимость консультанта в день
          const dailyRate = 1500; // USD
          consultingCost = consultingDays * dailyRate;
        }
        
        // Суммируем дополнительные затраты
        totalAdditionalCosts = maintenanceCost + trainingCost + licenseCost + consultingCost;
        
        // --- Расчет полной стоимости проекта ---
        // Базовая стоимость инфраструктуры и персонала
        const infrastructureCost = totalStorageCost + totalProcessingCost + totalSourceIntegrationCost;
        
        // Полная стоимость без непредвиденных расходов
        const subtotalWithoutContingency = infrastructureCost + totalPersonnelCost + totalAdditionalCosts;
        
        // Добавляем непредвиденные расходы
        const contingencyAmount = subtotalWithoutContingency * contingencyPercent;
        
        // Финальная стоимость проекта
        const totalProjectCost = subtotalWithoutContingency + contingencyAmount;
        
        // Рассчитываем стоимость за месяц
        const monthlyCost = totalProjectCost / projectDuration;
        
        // --- Результаты для распределения затрат ---
        const detailedCostBreakdown = {
          infrastructure: {
            storage: totalStorageCost,
            processing: totalProcessingCost,
            dataIntegration: totalSourceIntegrationCost,
            total: infrastructureCost
          },
          personnel: {
            total: totalPersonnelCost
          },
          additional: {
            maintenance: maintenanceCost,
            training: trainingCost,
            licensing: licenseCost,
            consulting: consultingCost,
            total: totalAdditionalCosts
          },
          summary: {
            subtotal: subtotalWithoutContingency,
            contingency: contingencyAmount,
            total: totalProjectCost
          }
        };
        
        // Распределение затрат в процентах
        const percentageBreakdown = {
          infrastructure: (infrastructureCost / totalProjectCost) * 100,
          personnel: (totalPersonnelCost / totalProjectCost) * 100,
          additional: (totalAdditionalCosts / totalProjectCost) * 100,
          contingency: (contingencyAmount / totalProjectCost) * 100
        };
        
        // --- Формируем результаты для отображения ---
        const finalResults = {
          totalCost: totalProjectCost,
          monthlyCost: monthlyCost,
          projectDuration: projectDuration,
          infrastructureCost: infrastructureCost,
          personnelCost: totalPersonnelCost,
          additionalCosts: totalAdditionalCosts,
          contingencyAmount: contingencyAmount,
          dataVolumeInTB: volumeInTB,
          percentageBreakdown: percentageBreakdown,
          currency: formData.currency
        };
        
        // Возвращаем результаты и детализацию затрат
        setResults(finalResults);
        setCostBreakdown(detailedCostBreakdown);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setCostBreakdown({});
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setCostBreakdown, setIsCalculating, projectTypes, cloudProviders, dataComplexityLevels, securityLevels, regionOptions]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      dataVolume: '1000',
      dataVolumeUnit: 'gb',
      dataProcessingRate: '100',
      projectDuration: '6',
      dataSourcesCount: '3',
      projectType: 'analytics',
      cloudProvider: 'aws',
      teamSize: '5',
      dataComplexity: 'medium',
      securityLevel: 'standard',
      includeMaintenanceCosts: true,
      maintenancePeriod: '12',
      includeTrainingCosts: false,
      trainingPeopleCount: '10',
      includeLicenseCosts: true,
      licenseType: 'per_user',
      licenseCount: '10',
      includeConsultingCosts: false,
      consultingDays: '10',
      region: 'us',
      currency: 'usd',
      contingencyPercent: '10',
    });
    setResults(null);
    setCostBreakdown({});
    setErrors({});
    setSaveProjectName('');
    setShowSaveDialog(false);
  };

  // Экспорт результатов расчета в CSV
  const exportToCSV = () => {
    if (!results || !costBreakdown) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Формируем заголовки 
    const mainRows = [
      ['Отчет о стоимости проекта Big Data'],
      [''],
      ['ОСНОВНЫЕ ПАРАМЕТРЫ'],
      ['Тип проекта:', projectTypes.find(t => t.id === formData.projectType)?.name || formData.projectType],
      ['Объем данных:', `${formData.dataVolume} ${formData.dataVolumeUnit.toUpperCase()}`],
      ['Скорость обработки:', `${formData.dataProcessingRate} ГБ/час`],
      ['Длительность проекта:', `${formData.projectDuration} месяцев`],
      ['Количество источников данных:', formData.dataSourcesCount],
      ['Размер команды:', formData.teamSize],
      ['Облачный провайдер:', cloudProviders.find(p => p.id === formData.cloudProvider)?.name || formData.cloudProvider],
      ['Регион размещения:', regionOptions.find(r => r.id === formData.region)?.name || formData.region],
      [''],
      ['ИТОГОВАЯ СТОИМОСТЬ'],
      ['Общая стоимость проекта:', formatCsvNumber(results.totalCost), getCurrencySymbol(formData.currency)],
      ['Ежемесячная стоимость:', formatCsvNumber(results.monthlyCost), getCurrencySymbol(formData.currency)],
      [''],
      ['ДЕТАЛИЗАЦИЯ ЗАТРАТ'],
      ['Инфраструктура:', formatCsvNumber(results.infrastructureCost), getCurrencySymbol(formData.currency), formatCsvNumber(results.percentageBreakdown.infrastructure) + '%'],
      ['Персонал:', formatCsvNumber(results.personnelCost), getCurrencySymbol(formData.currency), formatCsvNumber(results.percentageBreakdown.personnel) + '%'],
      ['Дополнительные расходы:', formatCsvNumber(results.additionalCosts), getCurrencySymbol(formData.currency), formatCsvNumber(results.percentageBreakdown.additional) + '%'],
      ['Непредвиденные расходы:', formatCsvNumber(results.contingencyAmount), getCurrencySymbol(formData.currency), formatCsvNumber(results.percentageBreakdown.contingency) + '%'],
      [''],
      ['ИНФРАСТРУКТУРА - ДЕТАЛИЗАЦИЯ'],
      ['Хранение данных:', formatCsvNumber(costBreakdown.infrastructure.storage), getCurrencySymbol(formData.currency)],
      ['Обработка данных:', formatCsvNumber(costBreakdown.infrastructure.processing), getCurrencySymbol(formData.currency)],
      ['Интеграция источников:', formatCsvNumber(costBreakdown.infrastructure.dataIntegration), getCurrencySymbol(formData.currency)],
      [''],
      ['ДОПОЛНИТЕЛЬНЫЕ РАСХОДЫ - ДЕТАЛИЗАЦИЯ'],
    ];

    const additionalRows = [];
    if (formData.includeMaintenanceCosts && costBreakdown.additional.maintenance > 0) {
      additionalRows.push(['Обслуживание системы:', formatCsvNumber(costBreakdown.additional.maintenance), getCurrencySymbol(formData.currency)]);
    }
    if (formData.includeTrainingCosts && costBreakdown.additional.training > 0) {
      additionalRows.push(['Обучение персонала:', formatCsvNumber(costBreakdown.additional.training), getCurrencySymbol(formData.currency)]);
    }
    if (formData.includeLicenseCosts && costBreakdown.additional.licensing > 0) {
      additionalRows.push(['Лицензирование:', formatCsvNumber(costBreakdown.additional.licensing), getCurrencySymbol(formData.currency)]);
    }
    if (formData.includeConsultingCosts && costBreakdown.additional.consulting > 0) {
      additionalRows.push(['Консалтинг:', formatCsvNumber(costBreakdown.additional.consulting), getCurrencySymbol(formData.currency)]);
    }

    const footerRows = [
      [''],
      ['Отчет сгенерирован:', new Date().toLocaleString()],
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...mainRows, ...additionalRows, ...footerRows].map(row => row.join(';')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_стоимости_big_data_проекта.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Вспомогательная функция для получения символа валюты
  const getCurrencySymbol = (currencyCode) => {
    switch (currencyCode.toLowerCase()) {
      case 'usd': return '$';
      case 'eur': return '€';
      case 'rub': return '₽';
      case 'gbp': return '£';
      default: return currencyCode;
    }
  };

  // Обработчик сохранения проекта
  const handleSaveProject = () => {
    if (showSaveDialog) {
      const savedResult = saveProject(saveProjectName || null);
      if (savedResult) {
        alert(`Проект "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProjectName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateProjectCost();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Database className="w-6 h-6 text-indigo-500" />
          Параметры проекта Big Data
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры проекта */}
        <ProjectParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          projectTypes={projectTypes}
        />

        {/* Инфраструктура и размещение */}
        <InfrastructureSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          cloudProviders={cloudProviders}
          regionOptions={regionOptions}
        />

        {/* Безопасность и сложность данных */}
        <SecuritySection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          dataComplexityLevels={dataComplexityLevels}
          securityLevels={securityLevels}
        />

        {/* Дополнительные расходы */}
        <AdditionalCostsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          licenseTypes={licenseTypes}
        />

        {/* Обслуживание системы */}
        <MaintenanceSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Диалог сохранения проекта */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProjectName}
                onChange={(e) => setSaveProjectName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProject}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать стоимость'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать отчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProject}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить проект
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;